/**
 * Authentication Service
 * Handles all authentication-related API calls
 * CD-271: Create API service layer
 */
import api from './api'

export default {
  /**
   * User login
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{access_token: string, user: object}>}
   */
  async login(email, password) {
    return api.post('/auth/login', { email, password })
  },

  /**
   * Admin login (same endpoint, different validation)
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @returns {Promise<{access_token: string, admin: object}>}
   */
  async adminLogin(email, password) {
    return api.post('/auth/admin/login', { email, password })
  },

  /**
   * Get current user profile
   * @returns {Promise<object>}
   */
  async getCurrentUser() {
    return api.get('/users/me')
  },

  /**
   * Get current admin profile
   * CD-362: Added for admin session persistence on page refresh
   * @returns {Promise<object>}
   */
  async getCurrentAdmin() {
    return api.get('/auth/admin/me')
  },

  /**
   * Initiate registration - sends OTP to email
   * @param {string} email - User email
   * @returns {Promise<{message: string}>}
   */
  async initiateRegistration(email) {
    return api.post('/auth/register/initiate', { email })
  },

  /**
   * Verify OTP during registration
   * @param {string} email - User email
   * @param {string} otp - 6-digit OTP code
   * @returns {Promise<{verification_token: string}>}
   */
  async verifyOtp(email, otp) {
    return api.post('/auth/register/verify', { email, otp_code: otp })
  },

  /**
   * Complete registration - set password
   * @param {string} verification_token - Token from OTP verification
   * @param {string} password - New password
   * @param {string} full_name - User's full name (optional)
   * @returns {Promise<{message: string}>}
   */
  async completeRegistration(verification_token, password, full_name = '') {
    return api.post('/auth/register/complete', {
      verification_token,
      password,
      full_name
    })
  },

  /**
   * Request password reset - sends email with reset link
   * @param {string} email - User email
   * @returns {Promise<{message: string}>}
   */
  async requestPasswordReset(email) {
    return api.post('/auth/password/reset-request', { email })
  },

  /**
   * Complete password reset
   * CD-386: Fixed field name to match backend schema (new_password instead of password)
   * @param {string} token - Reset token from email link
   * @param {string} password - New password
   * @returns {Promise<{message: string}>}
   */
  async resetPassword(token, password) {
    return api.post('/auth/password/reset', { token, new_password: password })
  },

  /**
   * Change password (authenticated user)
   * @param {string} current_password - Current password
   * @param {string} new_password - New password
   * @returns {Promise<{message: string}>}
   */
  async changePassword(current_password, new_password) {
    return api.post('/users/me/password', { current_password, new_password })
  },

  /**
   * Logout - clears server session if applicable
   * @returns {Promise<{message: string}>}
   */
  async logout() {
    return api.post('/auth/logout')
  },

  /**
   * Resend OTP during registration
   * @param {string} email - User email
   * @returns {Promise<{message: string}>}
   */
  async resendOtp(email) {
    return api.post('/auth/register/resend-otp', { email })
  },

  /**
   * Reactivate a deactivated account
   * CD-395: Allows users to reactivate their deactivated account
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{message: string, status: string, access_token: string}>}
   */
  async reactivateAccount(email, password) {
    return api.post('/auth/reactivate', { email, password })
  },

  /**
   * Get configured social login providers
   * Returns list of providers with login_url for OAuth initiation
   * Public endpoint — called on Login and Register pages
   * @returns {Promise<{providers: Array<{name: string, label: string, login_url: string}>}>}
   */
  async getSocialProviders() {
    return api.get('/auth/social/providers')
  },

  /**
   * Complete user onboarding after social sign-up
   * Idempotent — safe to call multiple times
   * @param {{full_name?: string, timezone?: string, first_domain?: string}} data
   * @returns {Promise<{message: string}>}
   */
  async completeOnboarding(data) {
    return api.post('/users/onboarding', data)
  }
}
